/**
 * Zone configuration for Kevel ad placements. Each zone maps to specific targeting
 * parameters (zone ID, ad types) and rendering requirements (aspect ratio, component).
 *
 * Zone IDs differ per Kevel site (dev vs. prod), so they come from runtime config
 * (`KEVEL_ZONE_*` env / PWA variables). Ad type IDs are the same on every site and stay here.
 */

import { useRuntimeConfig } from '#imports'

type KevelZoneRuntimeKey = 'contentBannerText' | 'contentBannerProducts' | 'plpSponsored' | 'cartTeaser' | 'cartSponsored'

interface KevelZoneDefinition {
    /** Human-readable zone name (matches admin config and CMS element config). */
    name: string
    /** Key of the zone ID in `runtimeConfig.public.kevelZones`. */
    runtimeKey: KevelZoneRuntimeKey
    /** Kevel ad type IDs this zone accepts. */
    adTypes: number[]
    /** CSS aspect-ratio value for reserving space (e.g., "16/9", "6/1"). */
    aspectRatio: string
    /** Tailwind aspect ratio class for fallback (e.g., "aspect-video", "aspect-[6/1]"). */
    aspectRatioClass: string
}

export interface KevelZoneConfig extends Omit<KevelZoneDefinition, 'runtimeKey'> {
    /** Kevel zone ID for targeting. */
    zoneId: number
}

/**
 * All configured zones. Keys match the zone values from CmsElementKevelAd config.
 */
const KEVEL_ZONES: Record<string, KevelZoneDefinition> = {
    'content-banner-text': {
        name: 'content-banner-text',
        runtimeKey: 'contentBannerText',
        adTypes: [495], // A4G 16:9 (1280x720)
        aspectRatio: '16/9',
        aspectRatioClass: 'aspect-video',
    },
    'content-banner-products': {
        name: 'content-banner-products',
        runtimeKey: 'contentBannerProducts',
        adTypes: [495], // A4G 16:9 (1280x720)
        aspectRatio: '16/9',
        aspectRatioClass: 'aspect-video',
    },
    'plp-sponsored': {
        name: 'plp-sponsored',
        runtimeKey: 'plpSponsored',
        adTypes: [20, 495], // Sponsored Listing (dimensionless) + A4G 16:9 (1280x720) - temporarily allowing 495 for testing
        aspectRatio: 'auto',
        aspectRatioClass: '', // No aspect ratio reservation for dimensionless ads
    },
    'cart-teaser': {
        name: 'cart-teaser',
        runtimeKey: 'cartTeaser',
        adTypes: [3253], // A4G 6:1 (1200x200)
        aspectRatio: '6/1',
        aspectRatioClass: 'aspect-[6/1]',
    },
    'cart-sponsored': {
        name: 'cart-sponsored',
        runtimeKey: 'cartSponsored',
        adTypes: [20, 495], // Sponsored Listing (dimensionless) + A4G 16:9 (1280x720) - temporarily allowing 495 for testing
        aspectRatio: 'auto',
        aspectRatioClass: '', // No aspect ratio reservation for dimensionless ads
    },
}

/**
 * Get zone configuration by zone name. Returns null for an unknown zone (warns: a typo) or a
 * zone without a configured ID. A missing ID deliberately disables the zone (e.g. a zone not
 * yet created on the prod Kevel site), so the placement is skipped silently — no request, and
 * hosts hide its reserved space.
 * Must be called in a setup context (reads runtime config).
 */
export function getKevelZoneConfig(zoneName: string): KevelZoneConfig | null {
    const definition = KEVEL_ZONES[zoneName]
    if (definition == null) {
        console.warn(`[kevel] Unknown zone: ${zoneName}`)
        return null
    }

    // Env values arrive as strings (PWA variables) or numbers (NUXT_* runtime overrides)
    const zoneId = Number(useRuntimeConfig().public.kevelZones[definition.runtimeKey])
    if (!Number.isInteger(zoneId) || zoneId <= 0) {
        return null
    }

    const { runtimeKey: _runtimeKey, ...zone } = definition
    return { ...zone, zoneId }
}
